import { Box, BoxProps, styled } from '@mui/material';
import { Children, ReactElement, createContext, useContext, useEffect, useState } from 'react';
import { noop } from '@synergy/utils';

export type PoolAccordionVariant = 'small' | 'medium' | 'large';

interface AccordionContextProps {
    index: number;
    activeIndex: number | null;
    setActiveIndex: (index: number | null) => void;
    variant?: PoolAccordionVariant;
}

const AccordionContext = createContext<AccordionContextProps>({
    index: 0,
    activeIndex: 0,
    setActiveIndex: noop,
});

export const useAccordionContext = (): AccordionContextProps => useContext(AccordionContext);

const StyledBox = styled(Box)(({ theme }) => ({
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
}));

export interface PoolAccordionProps extends BoxProps {
    activeIndex?: number;
    variant?: PoolAccordionVariant;
}

/**
 * PoolAccordion<br>
 * <a href="https://www.figma.com/file/kjfAT06qgLdW52cF1SqWuy/Pool-Alpin-Shop-2.0-Library?node-id=641%3A11236" target="_blank">Open in Figma</a><br />
 * <a href="https://www.figma.com/file/UXG8mmJQdrAX2oDRp0FTBl/Pool-Synergy-Shop?node-id=4152%3A219207" target="_blank">Open in Figma (Product Detail Page)</a>
 */
export const PoolAccordion = (props: PoolAccordionProps): ReactElement => {
    const { activeIndex: propsActiveIndex, variant, ...boxProps } = props;
    const [activeIndex, setActiveIndex] = useState<number | null>(propsActiveIndex ?? null);

    useEffect(() => {
        if (propsActiveIndex) {
            setActiveIndex(propsActiveIndex);
        }
    }, [propsActiveIndex]);

    return (
        <StyledBox {...boxProps}>
            {Children.map(props.children, (child, index) => (
                <AccordionContext.Provider
                    key={`panel-${index}`}
                    value={{ index, activeIndex, setActiveIndex, variant: variant ?? 'medium' }}
                >
                    {child}
                </AccordionContext.Provider>
            ))}
        </StyledBox>
    );
};
